import { useEffect, useState, useMemo } from "react"
import { useEntitlexApi } from "./useEntitlexApi"
import { entitlexService } from "../../services/entitlex"

/**
 * @param T
 * @typedef {(api: import("../../api/entitlex").EntitlexApi) => Promise<T>} EntitlexApiOperation
 */

/**
 * @param {EntitlexApiOperation} operation
 */
export const useEntitlexData = (operation) => {
  const [state, setState] = useState({ result: null, error: null, isRunning: false })
  const api = useEntitlexApi()

  useEffect(() => {
    async function execute() {
      if (!api) return
      let error = null
      let result = null
      setState({
        result,
        error,
        isRunning: true
      })

      try {
        result = await operation(api)
      } catch (e) {
        error = e
      } finally {
        if (!ignore) {
          setState({
            result,
            error,
            isRunning: false
          })
        }
      }
    }

    let ignore = false
    execute()
    return () => {
      ignore = true
    }
  }, [api, operation])

  return [state.result, state.isRunning, state.error]
}

export const useEntitlex = (email, applicationName, listHref) => {
  const [error, setError] = useState()

  const [availability, isAvailabilityLoading, availabilityError] = useEntitlexData(
    useMemo(() => entitlexService.init(), [])
  )

  const { hasAdminAccess, isAvailable } = availability || {}

  const [applications, isAppsLoading, appError] = useEntitlexData(
    useMemo(
      () => (hasAdminAccess ? entitlexService.getUserApplications(email) : () => null),
      [email, hasAdminAccess]
    )
  )

  const [permissions, isPermissionsLoading, permissionsError] = useEntitlexData(
    useMemo(() => entitlexService.getUserApplicationPermissions(applicationName), [applicationName])
  )

  const [list, isListLoading, listError] = useEntitlexData(
    useMemo(() => entitlexService.getListContent(listHref), [listHref])
  )

  useEffect(() => {
    setError(availabilityError || appError || permissionsError || listError)
  }, [appError, availabilityError, listError, permissionsError])

  return {
    error,
    resetError: () => setError(undefined),
    entitlex: {
      isAvailable,
      hasAdminAccess,
      isAvailabilityLoading
    },
    applications: {
      value: applications,
      loading: isAppsLoading
    },
    permissions: {
      value: permissions,
      loading: isPermissionsLoading
    },
    list: {
      value: list,
      loading: isListLoading
    }
  }
}
